// Vendor Libs
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {get, includes, isEmpty, pick} from 'lodash';

// Components
import Button from '../core/button';
import Error from '../core/error';
import FormConfirm from '../core/form-confirm';
import Loading from '../core/loading';
import RecordDetails from '../core/record-details';
import RulesForm from './rules-form';

// Containers
import withForm from '../../containers/with-form';
import withPermissions from '../../containers/with-permissions';

// Redux Actions
import {deleteRule, deleteRuleReset} from '../../actions/delete-rule-actions';
import {refreshServicesData, resetServiceData} from '../../actions/services-data-actions';

// Contexts
import {RouteContext} from '../../contexts/route-context';

// Styles
import './edit-rule.scss';

const formName = 'editRule';

class EditRule extends Component {
    state = {
        deleteConfirm: false,
        deleteCompleted: false,
        submitReset: false,
    };

    componentWillMount() {
        const {searchValues} = this.props;

        if (searchValues.ruleId) {
            this.setFormData();
        }
    }

    componentWillReceiveProps(nextProps) {
        const {searchValues} = this.props;
        const {submitReset} = this.state;

        if (searchValues.ruleId !== nextProps.searchValues.ruleId) {
            this.setFormData(nextProps);
            this.resetDeleteForm();
        } else if (submitReset) {
            this.setState({submitReset: false});
            this.setFormData(nextProps);
        }
    }

    getFormConfirmProps() {
        const {deleteCompleted} = this.state;
        const {deleteRuleError, deleteRuleResponse, searchValues} = this.props;

        return {
            completed: deleteCompleted,
            confirmMessage: 'Are you sure you want to delete this rule?',
            confirmLabel: 'Delete Rule',
            error: deleteRuleError,
            formSubmitReset: this.resetDeleteForm,
            onComplete: this.handleDeleteFormResetClick,
            onCancel: this.resetDeleteForm,
            onConfirm: this.handleDeleteButtonClick,
            response: deleteRuleResponse,
            searchData: searchValues,
            whitelistKeys: ['ruleId', 'name', 'category', 'status'],
        };
    }

    setFormData(props = this.props) {
        const {setFieldValues} = this.props;

        setFieldValues(pick(props.searchValues, ['ruleId', 'category', 'status', 'rule']));
    }

    canEdit = () => {
        const {editableCategories = ['ENRICH', 'PRE_PROD', 'VALIDATION'], hasAccessByType, searchValues} = this.props;

        return (
            hasAccessByType({accessRequirement: 'write'}) && includes(editableCategories, get(searchValues, 'category'))
        );
    };

    handleDeleteButtonClick = () => {
        const {deleteRule, refreshServicesData} = this.props;
        const {searchValues} = this.props;

        deleteRule(searchValues).then(() => {
            refreshServicesData('rules');
            this.setState({deleteConfirm: false, deleteCompleted: true});
        });
    };

    handleDeleteFormResetClick = () => {
        const {resetForm, resetServiceData} = this.props;
        const {push} = this.context;

        this.resetDeleteForm();
        resetForm();
        resetServiceData('rulesDetail');
        push('/rules');
    };

    handleDeleteShowFormClick = () => {
        this.setState({deleteConfirm: true, deleteCompleted: false});
    };

    handleResetForm = () => {
        this.setState({submitReset: true});
        this.setFormData();
    };

    resetDeleteForm = () => {
        const {deleteRuleReset} = this.props;

        deleteRuleReset();
        this.setState({deleteConfirm: false, deleteCompleted: false});
    };

    shouldRenderDeleteButton() {
        const {deleteCompleted, deleteConfirm} = this.state;
        const {deleteRuleFetching, hasAccessByType, searchValues} = this.props;

        return (
            hasAccessByType({accessRequirement: 'write'}) &&
            searchValues.ruleId &&
            !deleteRuleFetching &&
            !deleteConfirm &&
            !deleteCompleted
        );
    }

    shouldRenderFormConfirm() {
        const {deleteCompleted, deleteConfirm} = this.state;

        return deleteConfirm || deleteCompleted;
    }

    renderDeleteForm() {
        const {deleteCompleted} = this.state;

        return deleteCompleted ? this.renderDeleteFormCompleted() : this.renderDeleteFormConfirm();
    }

    renderDeleteShowFormButton() {
        const {searchValues} = this.props;
        const props = {
            className: 'edit-rule--button',
            disabled: get(searchValues, 'category') !== 'AUTO_MAP' && get(searchValues, 'status') !== 'INACTIVE',
            onClick: this.handleDeleteShowFormClick,
        };

        return this.shouldRenderDeleteButton() ? (
            <div className="edit-rule--buttons">
                <Button {...props}>Delete Rule</Button>
            </div>
        ) : null;
    }

    renderFields() {
        const {searchValues} = this.props;
        const formProps = {
            className: 'edit-rule',
            onReset: this.handleResetForm,
            noReset: true,
            name: formName,
            readOnly: !this.canEdit(),
        };

        return !isEmpty(searchValues) ? (
            <>
                {this.renderTextValues()}
                <RulesForm {...formProps} />
            </>
        ) : (
            <Error error="Select a rule to edit." />
        );
    }

    renderTextValues() {
        const {rulesBlacklistKeys = ['rule', 'status'], rulesWhitelistKeys = ['ruleId', 'name', 'category'], searchValues} = this.props;

        return searchValues.ruleId ? (
            <RecordDetails
                blacklistKeys={rulesBlacklistKeys}
                className="edit-rule--record-details"
                searchData={[searchValues]}
                whitelistKeys={rulesWhitelistKeys}
            />
        ) : null;
    }

    render() {
        const {deleteRuleFetching, searchFetching} = this.props;

        return (
            <div className="edit-rule">
                {this.renderDeleteShowFormButton()}
                {this.shouldRenderFormConfirm() ? <FormConfirm {...this.getFormConfirmProps()} /> : this.renderFields()}
                <Loading loading={deleteRuleFetching || searchFetching} />
            </div>
        );
    }
}

EditRule.propTypes = {
    deleteRule: PropTypes.func.isRequired,
    deleteRuleError: PropTypes.string,
    deleteRuleFetched: PropTypes.bool,
    deleteRuleFetching: PropTypes.bool,
    deleteRuleReset: PropTypes.func.isRequired,
    deleteRuleResponse: PropTypes.string,
    editableCategories: PropTypes.arrayOf(PropTypes.string),
    hasAccessByType: PropTypes.func.isRequired,
    refreshServicesData: PropTypes.func.isRequired,
    resetForm: PropTypes.func.isRequired,
    resetServiceData: PropTypes.func.isRequired,
    ruleId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    rulesBlacklistKeys: PropTypes.array,
    rulesError: PropTypes.bool,
    rulesFetching: PropTypes.bool,
    rulesWhitelistKeys: PropTypes.array,
    searchFetching: PropTypes.bool,
    searchValues: PropTypes.object,
    setFieldValues: PropTypes.func.isRequired, // form-wrapper
};

EditRule.contextType = RouteContext;

const mapStateToProps = (state) => ({
    deleteRuleError: state.deleteRule.error,
    deleteRuleFetching: state.deleteRule.fetching,
    deleteRuleFetched: state.deleteRule.fetched,
    deleteRuleResponse: state.deleteRule.response,
});

const mapDispatchToProps = {
    deleteRule,
    deleteRuleReset,
    refreshServicesData,
    resetServiceData,
};

export default withPermissions(withForm(connect(mapStateToProps, mapDispatchToProps)(EditRule), formName));
